import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUser, deleteUser, createUser, sendPasswordReset, uploadW9, deleteW9, setUserPassword } from '../../services/user.service';
import { projectService } from '../../services/project.service';
import { organizationService } from '../../services/organization.service';
import { User, UserRole, CreateUserDto } from '../../types/user.types';
import { Organization } from '../../types/organization.types';
import { toast } from 'react-hot-toast';
import { Edit, Trash2, UserPlus, Mail, Phone, Search, Key, Building2, Shield, AlertCircle, Loader2, FileText, Upload, Download, X, FolderOpen, Lock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { collection, query, where, orderBy, limit, startAfter, getDocs, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PAGE_SIZE = 50;

const UserManagementPage = () => {
  const { userProfile, currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  // Guards the Create button — a double-click double-called the createUser
  // callable (second call correctly 409s with email-already-exists).
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState<CreateUserDto>({
    email: '',
    name: '',
    role: 'field',
    organization: userProfile?.organization || '',
    projects: [],
    phoneNumber: '',
    sendPasswordReset: true
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingUserOriginalProjects, setEditingUserOriginalProjects] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [projectOptions, setProjectOptions] = useState<{ id: string; name: string; organizationId?: string }[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [editOrgId, setEditOrgId] = useState<string>('');
  const [editOrgName, setEditOrgName] = useState<string>('');
  const [editSetPrimaryAdmin, setEditSetPrimaryAdmin] = useState(false);
  const [editCurrentPrimaryAdmin, setEditCurrentPrimaryAdmin] = useState<{ id?: string; name?: string } | null>(null);
  // New user organization state
  const [newOrgId, setNewOrgId] = useState<string>('');
  const [newOrgName, setNewOrgName] = useState<string>('');
  const [newSetPrimaryAdmin, setNewSetPrimaryAdmin] = useState(false);
  const [newCurrentPrimaryAdmin, setNewCurrentPrimaryAdmin] = useState<{ id?: string; name?: string } | null>(null);
  // W9 document state
  const [w9Uploading, setW9Uploading] = useState(false);
  const [w9Deleting, setW9Deleting] = useState(false);
  // Project names lookup for display in user cards
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  // Password set state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordTargetUser, setPasswordTargetUser] = useState<User | null>(null);

  const isProviderAdmin = userProfile?.role === 'provider_admin';
  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  // Load organizations for provider_admin
  const loadOrganizations = async () => {
    if (isProviderAdmin) {
      try {
        const orgs = await organizationService.getActive();
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    }
  };

  // Load project names for displaying in user cards
  const loadProjectNames = async (usersList: User[]) => {
    const allProjectIds = new Set<string>();
    usersList.forEach(u => u.projects?.forEach(pid => allProjectIds.add(pid)));

    if (allProjectIds.size === 0) return;

    const names: Record<string, string> = {};
    for (const pid of allProjectIds) {
      try {
        const project = await projectService.getProject(pid);
        if (project) names[pid] = project.name;
      } catch (error) {
        // Project may have been deleted
        console.error(`Error loading project ${pid}:`, error);
      }
    }
    setProjectNames(names);
  };

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(lowerSearch) ||
        user.email.toLowerCase().includes(lowerSearch) ||
        (user.phoneNumber || '').toLowerCase().includes(lowerSearch)
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.isActive === (statusFilter === 'active'));
    }

    if (organizationFilter !== 'all') {
      filtered = filtered.filter(user => user.organization === organizationFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, statusFilter, organizationFilter]);

  // Initialize edit state when editing user changes
  useEffect(() => {
    if (editingUser) {
      setEditingUserOriginalProjects(editingUser.projects || []);
      setEditOrgId(editingUser.organizationId || '');
      setEditOrgName(editingUser.organizationName || '');
      const org = organizations.find(o => o.id === editingUser.organizationId);
      if (org) {
        setEditCurrentPrimaryAdmin({ id: org.primaryAdminId, name: org.primaryAdminName });
        setEditSetPrimaryAdmin(org.primaryAdminId === editingUser.uid);
      } else {
        setEditCurrentPrimaryAdmin(null);
        setEditSetPrimaryAdmin(false);
      }
    }
  }, [editingUser, organizations]);

  const loadUsers = useCallback(async (loadMore = false) => {
    if (!currentUser || !userProfile) {
      setLoading(false);
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLastDoc(null);
    }

    try {
      let usersQuery;

      if (userProfile.role === 'provider_admin' || userProfile.role === 'provider_user') {
        // Provider sees all users (no org filter)
        usersQuery = loadMore && lastDoc
          ? query(
              collection(db, 'users'),
              orderBy('createdAt', 'desc'),
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(
              collection(db, 'users'),
              orderBy('createdAt', 'desc'),
              limit(PAGE_SIZE)
            );
      } else if (userProfile.role === 'client_admin') {
        // Client admin sees only their org
        usersQuery = loadMore && lastDoc
          ? query(
              collection(db, 'users'),
              where('organizationId', '==', userProfile.organizationId),
              orderBy('createdAt', 'desc'),
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(
              collection(db, 'users'),
              where('organizationId', '==', userProfile.organizationId),
              orderBy('createdAt', 'desc'),
              limit(PAGE_SIZE)
            );
      } else {
        // Field or client_user sees only self
        setUsers([userProfile as User]);
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const snapshot = await getDocs(usersQuery);
      const newUsers = snapshot.docs.map(doc => ({
        ...doc.data()
      } as User));

      // Track pagination state
      setLastDoc(snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);

      if (loadMore) {
        const allUsers = [...users, ...newUsers];
        setUsers(allUsers);
        loadProjectNames(allUsers);
      } else {
        setUsers(newUsers);
        loadProjectNames(newUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile, lastDoc]);

  const loadProjectOptions = async () => {
    try {
      // Provider admins assign across all projects; other admins see their
      // org's. Exclude archived from the assignment options.
      let allProjects;
      if (userProfile?.role === 'provider_admin') {
        allProjects = await projectService.getAllProjects();
      } else if (userProfile?.organizationId) {
        allProjects = await projectService.getOrganizationProjects(userProfile.organizationId);
      } else {
        allProjects = await projectService.getUserProjects(userProfile?.uid || '');
      }
      setProjectOptions(
        allProjects.filter(p => p.status !== 'archived').map(p => ({ id: p.id, name: p.name, organizationId: p.organizationId }))
      );
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects for selection');
    }
  };

  // Load initial data when userProfile is available
  useEffect(() => {
    if (userProfile) {
      loadUsers();
      loadProjectOptions();
      loadOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingUser) return;
    setCreatingUser(true);
    try {
      // Include organization info
      const createData = {
        ...newUserData,
        organizationId: newOrgId || null,
        organizationName: newOrgName || null
      };

      const newUser = await createUser(createData);

      // Sync: add new user to each assigned project's assignedUsers[]
      if (newUser?.uid && newUserData.projects && newUserData.projects.length > 0) {
        await projectService.syncUserProjectAssignments(
          newUser.uid,
          newUserData.role,
          [], // old projects (none — new user)
          newUserData.projects
        );
      }

      // Handle primary admin assignment after user is created
      if (newSetPrimaryAdmin && newOrgId && newUser?.uid) {
        await organizationService.setPrimaryAdmin(newOrgId, newUser.uid, newUserData.name);
      }

      setShowNewUserForm(false);
      setNewUserData({
        email: '',
        name: '',
        role: 'field',
        organization: userProfile?.organization || '',
        projects: [],
        phoneNumber: '',
        sendPasswordReset: true
      });
      // Reset org state
      setNewOrgId('');
      setNewOrgName('');
      setNewSetPrimaryAdmin(false);
      setNewCurrentPrimaryAdmin(null);

      loadUsers();
      loadOrganizations();
      toast.success('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      await updateUser(editingUser.uid, {
        name: editingUser.name,
        role: editingUser.role,
        phoneNumber: editingUser.phoneNumber,
        projects: editingUser.projects,
        payRate: editingUser.payRate,
        organizationId: editOrgId || null,
        organizationName: editOrgName || null
      });

      // Sync: update project.assignedUsers[] for added/removed projects
      await projectService.syncUserProjectAssignments(
        editingUser.uid,
        editingUser.role,
        editingUserOriginalProjects,
        editingUser.projects || []
      );

      // Handle primary admin assignment
      if (editOrgId && isProviderAdmin) {
        const wasAlreadyPrimaryAdmin = editCurrentPrimaryAdmin?.id === editingUser.uid;

        if (editSetPrimaryAdmin && !wasAlreadyPrimaryAdmin) {
          // Set this user as primary admin
          await organizationService.setPrimaryAdmin(editOrgId, editingUser.uid, editingUser.name);
        } else if (!editSetPrimaryAdmin && wasAlreadyPrimaryAdmin) {
          // Remove this user as primary admin
          await organizationService.clearPrimaryAdmin(editOrgId);
        }
      }

      setEditingUser(null);
      loadUsers();  // Reload to ensure payRate displays
      loadOrganizations(); // Reload orgs to update primary admin info
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  // Handle organization change in edit modal
  const handleEditOrgChange = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    setEditOrgId(orgId);
    setEditOrgName(org?.name || '');

    if (org) {
      setEditCurrentPrimaryAdmin({ id: org.primaryAdminId, name: org.primaryAdminName });
      setEditSetPrimaryAdmin(org.primaryAdminId === editingUser?.uid);
    } else {
      setEditCurrentPrimaryAdmin(null);
      setEditSetPrimaryAdmin(false);
    }
  };

  // Handle organization change in new user modal
  const handleNewOrgChange = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    setNewOrgId(orgId);
    setNewOrgName(org?.name || '');

    if (org) {
      setNewCurrentPrimaryAdmin({ id: org.primaryAdminId, name: org.primaryAdminName });
    } else {
      setNewCurrentPrimaryAdmin(null);
    }
    setNewSetPrimaryAdmin(false);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        loadUsers();
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleSendPasswordReset = async (email: string) => {
    try {
      await sendPasswordReset(email);
      toast.success('Password reset email sent');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Failed to send password reset');
    }
  };

  const handleOpenPasswordModal = (user: User) => {
    setPasswordTargetUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async () => {
    if (!passwordTargetUser || !newPassword) return;

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSettingPassword(true);
    try {
      await setUserPassword(passwordTargetUser.uid, newPassword);
      toast.success(`Password set for ${passwordTargetUser.name}`);
      setShowPasswordModal(false);
      setNewPassword('');
      setPasswordTargetUser(null);
    } catch (error: any) {
      console.error('Error setting password:', error);
      toast.error(error.message || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleW9Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingUser || !e.target.files?.length) return;

    const file = e.target.files[0];

    // Validate file type (PDF, PNG, JPG)
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF, PNG, or JPG file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setW9Uploading(true);
    try {
      const result = await uploadW9(editingUser.uid, file);
      setEditingUser({
        ...editingUser,
        w9DocumentUrl: result.url,
        w9FileName: result.fileName,
        w9UploadedAt: { toDate: () => new Date() } as any
      });
      toast.success('W9 document uploaded successfully');
    } catch (error) {
      console.error('Error uploading W9:', error);
      toast.error('Failed to upload W9 document');
    } finally {
      setW9Uploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleW9Delete = async () => {
    if (!editingUser) return;

    if (!window.confirm('Are you sure you want to delete this W9 document?')) {
      return;
    }

    setW9Deleting(true);
    try {
      await deleteW9(editingUser.uid);
      setEditingUser({
        ...editingUser,
        w9DocumentUrl: undefined,
        w9FileName: undefined,
        w9UploadedAt: undefined
      });
      toast.success('W9 document deleted successfully');
    } catch (error) {
      console.error('Error deleting W9:', error);
      toast.error('Failed to delete W9 document');
    } finally {
      setW9Deleting(false);
    }
  };

  const getAllowedRoles = (): UserRole[] => {
    switch (userProfile?.role) {
      case 'provider_admin':
        return ['field', 'provider_user', 'client_admin', 'client_user'];
      case 'provider_user':
        return ['field', 'client_admin', 'client_user'];
      case 'client_admin':
        return ['client_user'];
      default:
        return [];
    }
  };

  const canEditUser = (targetUser: User) => {
    if (userProfile?.role === 'provider_user' && targetUser.role === 'provider_admin') {
      return false;
    }
    return true;
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'field': return 'bg-green-100 text-green-800';
      case 'provider_admin': return 'bg-indigo-100 text-indigo-800';
      case 'provider_user': return 'bg-amber-100 text-amber-800';
      case 'client_admin': return 'bg-purple-100 text-purple-800';
      case 'client_user': return 'bg-sky-100 text-sky-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRoleName = (role: UserRole) => {
    switch (role) {
      case 'field': return 'Field';
      case 'provider_admin': return 'Provider Admin';
      case 'provider_user': return 'Provider User';
      case 'client_admin': return 'Client Admin';
      case 'client_user': return 'Client User';
      default: return role;
    }
  };

  const getOrganizations = () => {
    const orgs = new Set(users.map(user => user.organization));
    return Array.from(orgs);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading users...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <button
          onClick={() => setShowNewUserForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md flex items-center"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center space-x-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border rounded-md"
            />
          </div>
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 border rounded-md"
        >
          <option value="all">All Roles</option>
          {getAllowedRoles().map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-md"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={organizationFilter}
          onChange={(e) => setOrganizationFilter(e.target.value)}
          className="px-4 py-2 border rounded-md"
        >
          <option value="all">All Organizations</option>
          {getOrganizations().map(org => (
            <option key={org} value={org}>{org}</option>
          ))}
        </select>
      </div>

      {/* User Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map(user => (
          <div key={user.uid} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden">
            {/* Header with name and actions */}
            <div className="px-5 py-4 border-b border-gray-50 bg-gray-50/50">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{user.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getRoleColor(user.role)}`}>
                    {formatRoleName(user.role)}
                  </span>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {canEditUser(user) && (
                    <button
                      onClick={() => setEditingUser(user)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit user"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteUser(user.uid)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete user"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center text-sm">
                <Mail className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-700 truncate" title={user.email}>{user.email}</span>
              </div>
              {user.phoneNumber && (
                <div className="flex items-center text-sm">
                  <Phone className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-700">{user.phoneNumber}</span>
                </div>
              )}
              <div className="pt-2 border-t border-gray-100 space-y-1.5 text-xs text-gray-500">
                <p>
                  <span className="font-medium text-gray-600">Organization:</span>{' '}
                  {user.organizationName || user.organization || 'None'}
                </p>
                <p>
                  <span className="font-medium text-gray-600">Status:</span>{' '}
                  <span className={user.isActive ? 'text-green-600' : 'text-red-600'}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
                <p>
                  <span className="font-medium text-gray-600">Created:</span>{' '}
                  {formatDistanceToNow(user.createdAt.toDate(), { addSuffix: true })}
                </p>
                <p className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">W9:</span>{' '}
                  {user.w9DocumentUrl ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      On file
                    </span>
                  ) : (
                    <span className="text-amber-600">Not uploaded</span>
                  )}
                </p>
                {user.projects && user.projects.length > 0 && (
                  <p className="flex items-start gap-1">
                    <span className="font-medium text-gray-600 flex items-center gap-1">
                      <FolderOpen className="h-3 w-3" />
                      Projects:
                    </span>{' '}
                    <span className="text-indigo-600">
                      {user.projects.map(pid => projectNames[pid]).filter(Boolean).join(', ') || 'None assigned'}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && !loading && (
        <div className="flex justify-center py-6">
          <button
            onClick={() => loadUsers(true)}
            disabled={loadingMore}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>Load More ({users.length} loaded)</>
            )}
          </button>
        </div>
      )}

      {/* New User Modal */}
      {showNewUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Add New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={newUserData.phoneNumber}
                  onChange={(e) => setNewUserData({...newUserData, phoneNumber: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newUserData.role}
                  onChange={(e) => setNewUserData({...newUserData, role: e.target.value as UserRole})}
                  className="w-full px-3 py-2 border rounded"
                >
                  {getAllowedRoles().map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              {/* Organization Selection - only for provider_admin */}
              {isProviderAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="flex items-center gap-1.5">
                      <Building2 className="h-4 w-4" />
                      Organization
                    </span>
                  </label>
                  <select
                    value={newOrgId}
                    onChange={(e) => handleNewOrgChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">No organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Primary Admin Toggle - only when org is selected */}
              {isProviderAdmin && newOrgId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newSetPrimaryAdmin}
                      onChange={(e) => setNewSetPrimaryAdmin(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-amber-600 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        <Shield className="h-4 w-4 text-amber-600" />
                        Set as Primary Admin
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        Full access to fiscal data, contracts, and billing.
                      </p>
                      {newCurrentPrimaryAdmin?.id && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span>
                            Current: <strong>{newCurrentPrimaryAdmin.name || 'Unknown'}</strong>
                            {newSetPrimaryAdmin && ' (will be replaced)'}
                          </span>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* Pay Rate — provider-only (confidential: what we pay field staff) */}
              {isProvider && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pay Rate</label>
                  <input
                    type="number"
                    value={newUserData.payRate || ''}
                    onChange={(e) => setNewUserData({...newUserData, payRate: e.target.value ? Number(e.target.value) : null})}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current Pay Rate: {newUserData.payRate ? `$${newUserData.payRate}/hr` : 'Using Project Default'}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Projects</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                  {projectOptions
                    .filter(p => !newOrgId || p.organizationId === newOrgId)
                    .map(project => (
                    <div key={project.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={project.id}
                        checked={newUserData.projects.includes(project.id)}
                        onChange={(e) => {
                          const updatedProjects = e.target.checked
                            ? [...newUserData.projects, project.id]
                            : newUserData.projects.filter(id => id !== project.id);
                          setNewUserData({...newUserData, projects: updatedProjects});
                        }}
                        className="mr-2"
                      />
                      <label htmlFor={project.id}>{project.name}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowNewUserForm(false)}
                  className="px-4 py-2 text-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingUser}
                  className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingUser ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Edit User</h2>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  disabled
                  className="w-full px-3 py-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={editingUser.phoneNumber || ''}
                  onChange={(e) => setEditingUser({...editingUser, phoneNumber: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value as UserRole})}
                  className="w-full px-3 py-2 border rounded"
                >
                  {getAllowedRoles().map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              {/* Organization Selection - only for provider_admin */}
              {isProviderAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="flex items-center gap-1.5">
                      <Building2 className="h-4 w-4" />
                      Organization
                    </span>
                  </label>
                  <select
                    value={editOrgId}
                    onChange={(e) => handleEditOrgChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">No organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Primary Admin Toggle - only when org is selected */}
              {isProviderAdmin && editOrgId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editSetPrimaryAdmin}
                      onChange={(e) => setEditSetPrimaryAdmin(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-amber-600 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        <Shield className="h-4 w-4 text-amber-600" />
                        Set as Primary Admin
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        Full access to fiscal data, contracts, and billing.
                      </p>
                      {editCurrentPrimaryAdmin?.id && editCurrentPrimaryAdmin.id !== editingUser.uid && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span>
                            Current: <strong>{editCurrentPrimaryAdmin.name || 'Unknown'}</strong>
                            {editSetPrimaryAdmin && ' (will be replaced)'}
                          </span>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* Pay Rate — provider-only (confidential: what we pay field staff) */}
              {isProvider && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pay Rate</label>
                  <input
                    type="number"
                    value={editingUser.payRate ?? ''}  // Use ?? to show '' if null
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : null;
                      setEditingUser({...editingUser, payRate: value});
                    }}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current Pay Rate: {editingUser.payRate !== null ? `$${editingUser.payRate}/hr` : 'Using Project Default'}
                  </p>
                </div>
              )}

              {/* W9 Document Section */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center gap-1.5">
                    <FileText className="h-4 w-4" />
                    W9 Document
                  </span>
                </label>

                {editingUser.w9DocumentUrl ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-green-800 truncate">
                            W9 on file
                          </p>
                          <p className="text-xs text-green-600">
                            Uploaded: {editingUser.w9UploadedAt?.toDate?.()
                              ? formatDistanceToNow(editingUser.w9UploadedAt.toDate(), { addSuffix: true })
                              : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={editingUser.w9DocumentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                          title="View/Download W9"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <button
                          type="button"
                          onClick={handleW9Delete}
                          disabled={w9Deleting}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete W9"
                        >
                          {w9Deleting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="text-xs text-green-700 cursor-pointer hover:underline">
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleW9Upload}
                          disabled={w9Uploading}
                          className="hidden"
                        />
                        {w9Uploading ? 'Uploading...' : 'Replace with new document'}
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">No W9 document uploaded</p>
                        <p className="text-xs text-gray-500">PDF, PNG, or JPG (max 10MB)</p>
                      </div>
                      <label className={`flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg cursor-pointer hover:bg-indigo-700 transition-colors ${w9Uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleW9Upload}
                          disabled={w9Uploading}
                          className="hidden"
                        />
                        {w9Uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload W9
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Projects</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                  {projectOptions
                    .filter(p => !editingUser.organizationId || p.organizationId === editingUser.organizationId)
                    .map(project => (
                    <div key={project.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={project.id}
                        checked={editingUser.projects.includes(project.id)}
                        onChange={(e) => {
                          const updatedProjects = e.target.checked
                            ? [...editingUser.projects, project.id]
                            : editingUser.projects.filter(id => id !== project.id);
                          setEditingUser({...editingUser, projects: updatedProjects});
                        }}
                        className="mr-2"
                      />
                      <label htmlFor={project.id}>{project.name}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleSendPasswordReset(editingUser.email)}
                  className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded text-sm"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Send Reset Link
                </button>
                {isProvider && (
                  <button
                    type="button"
                    onClick={() => handleOpenPasswordModal(editingUser)}
                    className="flex items-center px-4 py-2 bg-gray-700 text-white rounded text-sm"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Set Password
                  </button>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Password Modal */}
      {showPasswordModal && passwordTargetUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96 shadow-xl">
            <h2 className="text-xl font-bold mb-4">Set Password</h2>
            <p className="text-sm text-gray-600 mb-4">
              Set a new password for <strong>{passwordTargetUser.name}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setPasswordTargetUser(null);
                  }}
                  disabled={settingPassword}
                  className="px-4 py-2 text-gray-600 rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSetPassword}
                  disabled={settingPassword || newPassword.length < 6}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {settingPassword && <Loader2 className="h-4 w-4 animate-spin" />}
                  {settingPassword ? 'Setting...' : 'Set Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;